/**
 * Hero Section Component
 *
 * Dark card hero with Mosans title, eyebrow badge, and CTA buttons.
 * Uses responsive font sizes for the main headline.
 */
"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { useRouter } from "next/navigation"
import dynamic from "next/dynamic"
import Link from "next/link"
import { T, Var, useLocale, useGT } from "gt-next"
import { AvatarGroup, AvatarGroupItem, AvatarGroupTooltip, AvatarGroupYou, SimpleAvatar } from "@/components/ui/avatar-group"
const MorphingHeroLine = dynamic(() => import("@/components/outfame/MorphingHeroLine"))
import IPhoneMockup from "@/components/landing/IPhoneMockup"
import { GrowthPreviewCardSkeleton } from "@/components/landing/GrowthPreviewCardSkeleton"
import { ConfettiBurst } from "@/components/landing/ConfettiBurst"
import { getFeaturedClients } from "@/utils/clients"
import { DEMO_ACCOUNT_ID } from "@/constants"

// Lazy load GrowthPreviewCard (code-split for Framer Motion bundle)
const GrowthPreviewCard = dynamic(() => import("@/components/landing/GrowthPreviewCard"), {
    loading: () => <GrowthPreviewCardSkeleton />
})

// Avatar data for social proof - using real clients from centralized system
const featuredClients = getFeaturedClients()
const AVATARS = featuredClients.slice(0, 6).map((client) => ({
    // Optimized copy at the photo's full 200px resolution (~5 KB WebP) instead of the raw ~18 KB JPG.
    // w=256 never upscales, so this is the same 200x200 image the raw file shows.
    src: `/_next/image?url=${encodeURIComponent(client.avatar)}&w=256&q=75`,
    name: client.name.split(" ")[0] + " " + (client.name.split(" ")[1]?.[0] || "") + ".",
    fallback: client.name.split(" ").map(n => n[0]).join(""),
    growth: client.metrics ? `+${Math.round((client.metrics.followersAfter - client.metrics.followersBefore) / 1000)}K followers` : "+12K followers",
    growthK: client.metrics ? Math.round((client.metrics.followersAfter - client.metrics.followersBefore) / 1000) : null
}))

// Hero line variants for morphing (platform-specific)
const HERO_LINES: Record<string, readonly string[]> = {
    en: ["Grow your Instagram", "Grow your TikTok"],
    es: ["Haz crecer tu Instagram", "Haz crecer tu TikTok"],
    fr: ["Développez votre Instagram", "Développez votre TikTok"],
    de: ["Wachse auf Instagram", "Wachse auf TikTok"],
    pt: ["Cresça no Instagram", "Cresça no TikTok"],
    ro: ["Crește-ți Instagram-ul", "Crește-ți TikTok-ul"]
} as const

// AI Sparkles icon component (inline SVG) with subtle animations
function AISparklesIcon({ className = "" }: { className?: string }) {
    return (
        <span aria-hidden="true" data-nosnippet="">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={`ai-icon-hover ${className}`} aria-hidden="true">
                {/* Rounded square bracket */}
                <path d="M4 7C4 5.34315 5.34315 4 7 4H12M4 17C4 18.6569 5.34315 20 7 20H12M20 12V17C20 18.6569 18.6569 20 17 20H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: "ai-shimmer 3s ease-in-out infinite" }} />
                {/* AI letters as paths instead of text element - prevents crawler extraction */}
                <path d="M12.2 15.5L11.1 12.7H8.9L7.8 15.5H6.5L9.1 8.5H10.9L13.5 15.5H12.2ZM10 10L9.2 12H10.8L10 10Z" fill="currentColor" />
                <path d="M14.5 8.5H15.8V15.5H14.5V8.5Z" fill="currentColor" />
                {/* Main sparkle */}
                <path className="sparkle-main" d="M18 2L18.5 4L20.5 4.5L18.5 5L18 7L17.5 5L15.5 4.5L17.5 4L18 2Z" fill="currentColor" style={{ animation: "ai-twinkle 2.5s ease-in-out infinite" }} />
                {/* Secondary sparkle */}
                <path className="sparkle-secondary" d="M21 7L21.3 8.2L22.5 8.5L21.3 8.8L21 10L20.7 8.8L19.5 8.5L20.7 8.2L21 7Z" fill="currentColor" style={{ animation: "ai-twinkle 2.5s ease-in-out 1s infinite", opacity: 0.6 }} />
            </svg>
        </span>
    )
}

interface HeroProps {
    eyebrow?: string
    eyebrowLink?: string
    title?: string
    subtitle?: string
    primaryCta?: {
        text: string
        href: string
    }
    secondaryCta?: {
        text: string
        href: string
    }
    note?: string
}

// Default text constants for translation
const DEFAULT_EYEBROW = "AI-Powered Instagram Growth"
const DEFAULT_SUBTITLE = "You don't need to post more. You need to get seen. We find people who already want to follow accounts like yours—and put you in front of them."
const DEFAULT_PRIMARY_CTA = "Start Growing Free"
const DEFAULT_SECONDARY_CTA = "See Live Demo"
const DEFAULT_NOTE = "Join 65,000+ creators already growing"

export default function Hero({ eyebrow, eyebrowLink = "#", title, subtitle, primaryCta = { text: DEFAULT_PRIMARY_CTA, href: "/setup" }, secondaryCta = { text: DEFAULT_SECONDARY_CTA, href: `https://demo.outfame.com/app/account/${DEMO_ACCOUNT_ID}` }, note }: HeroProps) {
    // Get current locale from gt-next
    const locale = useLocale()
    const router = useRouter()
    const gt = useGT()

    // Use defaults or custom props
    const showEyebrow = eyebrow ?? DEFAULT_EYEBROW
    const showSubtitle = subtitle ?? DEFAULT_SUBTITLE
    const showNote = note ?? DEFAULT_NOTE

    // Get locale-specific hero lines (fallback to English)
    const heroLines = HERO_LINES[locale] ?? HERO_LINES.en

    // Confetti state for profile load celebration
    const [confettiTrigger, setConfettiTrigger] = useState(false)
    const [confettiSource, setConfettiSource] = useState<{ x: number; y: number } | null>(null)
    const mockupRef = useRef<HTMLDivElement>(null)

    // Handle profile load - trigger confetti from top of iPhone mockup (notch area)
    // Confetti bursts UPWARD from here, then falls back down with gravity
    const handleProfileLoaded = useCallback(() => {
        if (mockupRef.current) {
            const rect = mockupRef.current.getBoundingClientRect()
            setConfettiSource({
                x: rect.left + rect.width / 2,
                y: rect.top + 60 // Top of mockup near notch - confetti launches UP from here
            })
        }
        setConfettiTrigger(true)
    }, [])

    // Reset trigger after animation completes to allow re-triggering
    const handleConfettiComplete = useCallback(() => {
        setConfettiTrigger(false)
    }, [])

    // Deferred prefetch of the main next routes. It waits until the page has finished loading
    // plus a quiet moment (or the visitor's first touch, click, key or scroll), so the route
    // payloads and their JS chunks never compete with the first paint. Running it in an idle
    // callback straight after hydration pulled ~300 KB of other routes' code into page load.
    useEffect(() => {
        let done = false
        let timer: ReturnType<typeof setTimeout> | undefined
        const interactionEvents = ["pointerdown", "touchstart", "keydown", "scroll"] as const
        const prefetchRoutes = () => {
            if (done) return
            done = true
            cleanup()
            for (const href of ["/setup", "/pricing", "/outfame-reviews"]) router.prefetch(href)
        }
        const onLoad = () => {
            timer = setTimeout(() => {
                if ("requestIdleCallback" in window) requestIdleCallback(prefetchRoutes, { timeout: 2000 })
                else prefetchRoutes()
            }, 3000)
        }
        function cleanup() {
            if (timer) clearTimeout(timer)
            window.removeEventListener("load", onLoad)
            for (const type of interactionEvents) window.removeEventListener(type, prefetchRoutes)
        }
        for (const type of interactionEvents) window.addEventListener(type, prefetchRoutes, { once: true, passive: true })
        if (document.readyState === "complete") onLoad()
        else window.addEventListener("load", onLoad, { once: true })
        return () => {
            done = true
            cleanup()
        }
    }, [router])

    return (
        <section className="overflow-x-clip bg-white px-3 pb-0" style={{ paddingTop: "12px" }}>
            <div className="w-full">
                {/* Dark hero card - header floats above this */}
                <div className="rounded-[20px] bg-ink px-4 pb-[140px] pt-[120px] sm:pb-[180px] md:pb-[240px] md:pt-[160px]">
                    <div className="flex flex-col items-center text-center">
                        {/* Eyebrow */}
                        {eyebrowLink && (
                            <Link href={eyebrowLink} className="mb-4 text-[15px] font-semibold leading-[20px] text-white/60 transition-colors hover:text-white/80 md:mb-6">
                                {eyebrow ? eyebrow : <T>AI-Powered Instagram Growth</T>}
                            </Link>
                        )}

                        {/* Main Headline - Two lines with morphing platform */}
                        <h1 className="hero-title mb-4 font-mosans font-normal leading-[90%] text-white md:mb-6">
                            {title ? (
                                title
                            ) : (
                                <>
                                    <MorphingHeroLine lines={heroLines} />
                                    {" "}
                                    <span className="block">
                                        <T>while you</T>{" "}
                                        <span className="relative inline-block">
                                            <T>sleep</T>
                                            <AISparklesIcon className="absolute -right-[0.6em] top-[0.02em] h-[0.56em] w-[0.56em] opacity-60 md:-right-[0.55em] md:top-[0.05em] md:h-[0.5em] md:w-[0.5em]" />
                                        </span>
                                    </span>
                                </>
                            )}
                        </h1>

                        {/* Hero description and CTA wrapper */}
                        <div className="flex max-w-[590px] flex-col items-center gap-6">
                            {/* Subheadline */}
                            <p className="text-center text-[18px] font-normal leading-[155%] text-white md:text-[20px] md:leading-[140%]">
                                {subtitle ? (
                                    subtitle
                                ) : (
                                    <>
                                        <T>You don't need to post more. You need to get seen.</T>
                                        {locale === "en" && <br className="hidden md:block" />}{" "}
                                        <T>No bots. Real followers who actually engage — automatically.</T>
                                    </>
                                )}
                            </p>

                            {/* CTA Section */}
                            <div className="flex flex-col items-center gap-3">
                                {/* CTA Buttons - width matches iPhone mockup below */}
                                <div className="mx-auto flex w-full max-w-[400px] flex-row items-center justify-center gap-2 px-4 sm:max-w-[420px] md:max-w-[433px]">
                                    <Link href={primaryCta.href} prefetch={false} className="hero-cta-button inline-flex items-center justify-center rounded-xl bg-[rgb(255,163,209)] font-bold text-ink transition-colors hover:bg-[#fc83bf]">
                                        <span>{primaryCta.text === DEFAULT_PRIMARY_CTA ? <T>Start Growing Free</T> : primaryCta.text}</span>
                                    </Link>
                                    <a href={secondaryCta.href} target="_blank" rel="noopener noreferrer" className="hero-cta-button bg-white/12 inline-flex items-center justify-center rounded-xl font-semibold text-white transition-colors hover:bg-white/20">
                                        <span>{secondaryCta.text === DEFAULT_SECONDARY_CTA ? <T>See Live Demo</T> : secondaryCta.text}</span>
                                    </a>
                                </div>
                                {/* Descriptive text */}
                                <p className="text-center text-[14px] font-normal text-white/60">{note ? note : <T>Join 65,000+ creators & brands already growing</T>}</p>

                                {/* Animated Avatar Group - Social Proof */}
                                <div data-nosnippet>
                                    <AvatarGroup className="mt-4" translate="-20%" side="top" sideOffset={8}>
                                        {AVATARS.map((avatar, index) => (
                                            <AvatarGroupItem key={index}>
                                                <SimpleAvatar src={avatar.src} alt={avatar.name} fallback={avatar.fallback} size="md" className="ring-ink" />
                                                <AvatarGroupTooltip>
                                                    <p className="font-medium">{avatar.name}</p>
                                                    <p className="text-[14px] text-emerald-400">{avatar.growthK !== null ? <T>+<Var name="count">{avatar.growthK}</Var>K followers</T> : <T>+12K followers</T>}</p>
                                                </AvatarGroupTooltip>
                                            </AvatarGroupItem>
                                        ))}
                                        <AvatarGroupYou size="md" tooltipText={gt("You could be next!")} />
                                    </AvatarGroup>
                                </div>

                                {/* Compact Rating Display */}
                                <Link href="/outfame-reviews" prefetch={false} className="mt-1.5 flex items-center gap-2 transition-opacity hover:opacity-80">
                                    {/* Google icon */}
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img src="https://fonts.gstatic.com/s/i/productlogos/googleg/v6/24px.svg" alt="Google" width={16} height={16} className="h-4 w-4" />
                                    <span className="text-[15px] font-bold text-white">4.9</span>
                                    {/* Mini stars */}
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="h-3.5 w-3.5 fill-yellow-400" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>
                                    <span className="text-[13px] text-white/60">
                                        <T>from <Var name="count"><span className="font-medium text-white/80">2140+</span></Var> reviews</T>
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Growth Preview Card in iPhone Mockup - Cropped at content boundary */}
                <div ref={mockupRef} className="relative z-10 mx-auto -mt-[120px] w-full max-w-[400px] px-4 sm:-mt-[160px] sm:max-w-[420px] md:-mt-[216px] md:max-w-[433px]">
                    {/* Arrow doodle annotation - desktop only, fades in after page load */}
                    <div className="pointer-events-none absolute -right-[240px] top-[20px] hidden select-none md:flex md:items-start xl:-right-[260px]">
                        {/* Curved arrow SVG - wrapper handles animation, inner handles rotation */}
                        <div className="opacity-0" style={{ animation: "doodle-fade-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.8s forwards" }}>
                            {/* eslint-disable-next-line @next/next/no-img-element -- decorative SVG, Next Image adds unnecessary JS overhead */}
                            <img
                                src="/img/icons/curved-arrow-doodle.svg"
                                alt=""
                                aria-hidden="true"
                                width={80}
                                height={80}
                                loading="lazy"
                                decoding="async"
                                fetchPriority="low"
                                className="h-[72px] w-[72px] rotate-[120deg] xl:h-[80px] xl:w-[80px]"
                            />
                        </div>
                        {/* Annotation text - wrapper handles animation */}
                        <div className="ml-5 opacity-0" style={{ animation: "doodle-fade-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.9s forwards" }}>
                            <span className="block max-w-[140px] text-[16px] font-semibold leading-[1.3] text-[rgb(255,163,209)] xl:text-[17px]">
                                <T>Get your growth estimate</T>
                            </span>
                        </div>
                    </div>
                    <div
                        style={{
                            aspectRatio: "433/450",
                            overflow: "hidden",
                            maskImage: "linear-gradient(to bottom, black 0%, black 88%, transparent 100%)",
                            WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 88%, transparent 100%)"
                        }}
                    >
                        <IPhoneMockup>
                            <GrowthPreviewCard insideMockup onProfileLoaded={handleProfileLoaded} />
                        </IPhoneMockup>
                    </div>
                </div>

                {/* Confetti burst when profile loads */}
                <ConfettiBurst
                    trigger={confettiTrigger}
                    onComplete={handleConfettiComplete}
                    sourceX={confettiSource?.x}
                    sourceY={confettiSource?.y}
                    sourceWidth={320}
                    zIndex={20}
                />
            </div>
        </section>
    )
}
